import { useMemo, useRef, useState } from 'react'
import { AlertTriangle, Loader2, Pencil, Plus, Trash2, Upload } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { flattenExample, parseExample, type FlatPath } from '@/lib/lis/flattenExample'
import type { LisNamedPath, LisSourceSchema } from '@/api/lisConfigApi'
import {
  useCreateLisNamedPath,
  useCreateLisSourceSchema,
  useLisSourceSchemas,
  useRemoveLisNamedPath,
  useRemoveLisSourceSchema,
  useUpdateLisNamedPath,
  useUpdateLisSourceSchema,
} from '@/hooks/useLisConfig'

/**
 * Pre-Mapping — give names to the parts of the source's message you map from.
 *
 * The integration has ONE source, registered by one real message it sends.
 * Nothing in a delivery says which source sent it, so a second source would be a
 * choice the system could never make; the server refuses one.
 *
 * The message is flattened into paths — the keys needed to reach each value,
 * concatenated — and a pre-mapping is a name for one of them. The Mapping tab
 * offers those names (alongside the message's top-level fields) as Source and
 * Value fields, so a deep path is chosen once, by name.
 */
export function LisPreMappingTab() {
  const schemas = useLisSourceSchemas()

  if (schemas.isPending) return <p className='text-sm text-muted-foreground'>Loading&hellip;</p>

  const rows = schemas.data ?? []
  // Tenants configured before the one-source rule may hold several. The first
  // is the one in use; the rest are offered for removal below.
  const [source, ...extras] = rows

  if (!source) return <ExampleForm title='Add the source' onDone={() => undefined} />

  return (
    <div className='space-y-4'>
      {extras.length > 0 ? <ExtraSourcesNotice source={source} extras={extras} /> : null}
      <SourcePanel key={source.id} schema={source} />
    </div>
  )
}

/** Sources beyond the first, from before the one-source rule. */
function ExtraSourcesNotice({
  source,
  extras,
}: {
  source: LisSourceSchema
  extras: LisSourceSchema[]
}) {
  const remove = useRemoveLisSourceSchema()
  return (
    <div className='flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
      <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
      <div className='space-y-2'>
        <p>
          This integration has more than one source. Only <strong>{source.name}</strong> is used;
          nothing in a delivery says which source sent it. Remove the others:
        </p>
        <ul className='space-y-1'>
          {extras.map((s) => (
            <li key={s.id} className='flex items-center gap-2'>
              <span className='font-medium'>{s.name}</span>
              <span className='text-xs'>
                ({s.namedPaths.length} pre-mapping{s.namedPaths.length === 1 ? '' : 's'}
                {s._count?.preMaps ? `, ${s._count.preMaps} mapping(s)` : ''})
              </span>
              <Button
                size='sm'
                variant='outline'
                className='h-7'
                disabled={remove.isPending}
                onClick={() => remove.mutate(s.id)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/** Register the source, or replace its example. Paste or upload. */
function ExampleForm({
  title,
  schema,
  onDone,
  onCancel,
}: {
  title: string
  schema?: LisSourceSchema
  onDone: () => void
  onCancel?: () => void
}) {
  const [name, setName] = useState(schema?.name ?? '')
  const [text, setText] = useState(schema?.sample ? JSON.stringify(schema.sample, null, 2) : '')
  const fileRef = useRef<HTMLInputElement>(null)
  const create = useCreateLisSourceSchema()
  const update = useUpdateLisSourceSchema()
  const saving = create.isPending || update.isPending
  const error = create.error ?? update.error

  const parsed = useMemo(() => (text.trim() ? parseExample(text) : null), [text])
  const fields = useMemo(() => (parsed?.ok ? flattenExample(parsed.value) : []), [parsed])
  const fieldCount = fields.length

  // Which field is the order number and which the test code. Every message
  // carries both; WHERE is the source's business, so they are chosen here. A
  // field literally called `orderId` / `testCode` is offered first as a guess.
  const [orderIdPath, setOrderIdPath] = useState(schema?.orderIdPath ?? '')
  const [testCodePath, setTestCodePath] = useState(schema?.testCodePath ?? '')
  const guess = (wanted: string) => fields.find((f) => f.path === wanted)?.path ?? ''
  const orderField = orderIdPath || guess('orderId')
  const testField = testCodePath || guess('testCode')
  const fieldChoices = fields.map((f) => ({ value: f.path, label: `${f.path}` }))
  const sameField = !!orderField && orderField === testField

  const canSave =
    !!parsed?.ok && !!name.trim() && !!orderField && !!testField && !sameField && !saving

  const save = async () => {
    if (!canSave || !parsed?.ok) return
    const body = {
      name: name.trim(),
      sample: parsed.value,
      orderIdPath: orderField,
      testCodePath: testField,
    }
    if (schema) await update.mutateAsync({ id: schema.id, ...body })
    else await create.mutateAsync(body)
    onDone()
  }

  return (
    <div className='space-y-4 rounded-xl border bg-white p-4 shadow-sm'>
      <div>
        <h3 className='text-sm font-medium'>{title}</h3>
        <p className='mt-1 text-xs text-muted-foreground'>
          Paste or upload one message exactly as the laboratory sends it.
        </p>
      </div>

      <div className='max-w-sm space-y-1.5'>
        <Label htmlFor='lis-source-name' className='text-xs'>
          Source name
        </Label>
        <Input
          id='lis-source-name'
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder='e.g. Epic Beaker'
        />
      </div>

      <div className='space-y-1.5'>
        <div className='flex items-center justify-between'>
          <Label htmlFor='lis-source-example' className='text-xs'>
            Example message (JSON)
          </Label>
          <Button
            type='button'
            size='sm'
            variant='ghost'
            className='h-7 text-xs'
            onClick={() => fileRef.current?.click()}
          >
            <Upload className='mr-1.5 h-3.5 w-3.5' /> Upload .json
          </Button>
          <input
            ref={fileRef}
            type='file'
            accept='.json,application/json'
            className='hidden'
            onChange={async (e) => {
              const file = e.target.files?.[0]
              if (file) setText(await file.text())
              e.target.value = ''
            }}
          />
        </div>
        <Textarea
          id='lis-source-example'
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={12}
          spellCheck={false}
          placeholder='{ "orderId": "T21-…", "testCode": "CTCC", "observations": [ … ] }'
          className={cn('font-mono text-xs', parsed && !parsed.ok && 'border-destructive')}
        />
        {parsed && !parsed.ok ? (
          <p className='text-xs text-destructive'>{parsed.error}</p>
        ) : parsed?.ok ? (
          <p className='text-xs text-muted-foreground'>
            {fieldCount} field{fieldCount === 1 ? '' : 's'} found.
            {schema ? ' Existing pre-mappings are kept, even for fields this example lacks.' : ''}
          </p>
        ) : null}
      </div>

      {parsed?.ok ? (
        <div className='grid gap-3 sm:grid-cols-2'>
          <div className='grid gap-1.5'>
            <Label className='text-xs'>Order number field</Label>
            <SearchableSelect
              value={orderField}
              onChange={setOrderIdPath}
              options={fieldChoices}
              placeholder='Which field holds our order number?'
            />
          </div>
          <div className='grid gap-1.5'>
            <Label className='text-xs'>Test code field</Label>
            <SearchableSelect
              value={testField}
              onChange={setTestCodePath}
              options={fieldChoices}
              placeholder='Which field holds the test code?'
            />
          </div>
          {sameField ? (
            <p className='text-xs text-destructive sm:col-span-2'>
              The order number and the test code are different fields. Choose one for each.
            </p>
          ) : null}
        </div>
      ) : null}

      {error ? <p className='text-sm text-destructive'>{error.message}</p> : null}

      <div className='flex gap-2'>
        <Button size='sm' onClick={save} disabled={!canSave}>
          {saving ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
          {schema ? 'Save example' : 'Add source'}
        </Button>
        {onCancel ? (
          <Button size='sm' variant='ghost' onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    </div>
  )
}

/** The source: its pre-mappings as a table, and the form that adds to it. */
function SourcePanel({ schema }: { schema: LisSourceSchema }) {
  const [replacing, setReplacing] = useState(false)
  // null = closed; 'new' = creating; a row = editing it.
  const [editing, setEditing] = useState<LisNamedPath | 'new' | null>(null)
  const removeSource = useRemoveLisSourceSchema()
  const removeName = useRemoveLisNamedPath()

  const paths = useMemo(() => flattenExample(schema.sample), [schema.sample])
  const sampleOf = (path: string) => paths.find((p) => p.path === path)?.sample

  if (replacing) {
    return (
      <ExampleForm
        title={`Replace the example for ${schema.name}`}
        schema={schema}
        onDone={() => setReplacing(false)}
        onCancel={() => setReplacing(false)}
      />
    )
  }

  return (
    <div className='rounded-xl border bg-white shadow-sm'>
      <div className='flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3'>
        <div>
          <h3 className='text-sm font-medium'>{schema.name}</h3>
          {schema.orderIdPath && schema.testCodePath ? (
            <p className='mt-1 text-xs'>
              Order number: <span className='font-mono'>{schema.orderIdPath}</span>
              <span className='mx-2 text-muted-foreground'>·</span>
              Test code: <span className='font-mono'>{schema.testCodePath}</span>
            </p>
          ) : (
            <p className='mt-1 flex items-center gap-1.5 text-xs text-amber-700'>
              <AlertTriangle className='h-3.5 w-3.5' />
              Choose which fields hold the order number and the test code — use{' '}
              <strong>Replace example</strong>.
            </p>
          )}
          <p className='mt-1 text-xs text-muted-foreground'>
            Pre-mappings name nested fields you will map from. The Mapping tab offers them as Source
            and Value fields, beside the message&rsquo;s top-level fields, which need no name.
          </p>
        </div>
        <div className='flex gap-2'>
          <Button size='sm' onClick={() => setEditing('new')} disabled={editing !== null}>
            <Plus className='mr-2 h-4 w-4' /> Create pre-mapping
          </Button>
          <Button size='sm' variant='outline' onClick={() => setReplacing(true)}>
            Replace example
          </Button>
          <Button
            size='sm'
            variant='ghost'
            title={
              schema._count?.preMaps
                ? `Remove the source, its pre-mappings and ${schema._count.preMaps} mapping(s)`
                : 'Remove the source and its pre-mappings'
            }
            onClick={() => removeSource.mutate(schema.id)}
          >
            <Trash2 className='h-4 w-4' />
          </Button>
        </div>
      </div>

      {editing ? (
        <PreMappingForm
          key={editing === 'new' ? 'new' : editing.id}
          schema={schema}
          paths={paths}
          row={editing === 'new' ? null : editing}
          onDone={() => setEditing(null)}
        />
      ) : null}

      {schema.namedPaths.length === 0 ? (
        <p className='px-4 py-10 text-center text-sm text-muted-foreground'>
          No pre-mappings yet. Use <strong>Create pre-mapping</strong> to name a nested field.
        </p>
      ) : (
        <div className='overflow-x-persistent'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b text-left text-[11px] uppercase tracking-wider text-muted-foreground'>
                <th className='px-4 py-2 font-medium'>Name</th>
                <th className='px-4 py-2 font-medium'>Field</th>
                <th className='px-4 py-2 font-medium'>Example value</th>
                <th className='px-4 py-2 font-medium'></th>
              </tr>
            </thead>
            <tbody>
              {schema.namedPaths.map((n) => {
                const sample = sampleOf(n.path)
                return (
                  <tr key={n.id} className='border-b last:border-0'>
                    <td className='px-4 py-2 font-medium'>{n.name}</td>
                    <td className='px-4 py-2 font-mono text-xs'>{n.path}</td>
                    <td className='max-w-[16rem] truncate px-4 py-2 text-xs text-muted-foreground' title={sample}>
                      {sample ?? (
                        // The laboratory may send this field again; the name is
                        // kept, and only flagged.
                        <Badge variant='outline' className='text-[10px] text-amber-700'>
                          not in the example
                        </Badge>
                      )}
                    </td>
                    <td className='px-4 py-2 text-right'>
                      <div className='flex items-center justify-end gap-2'>
                        <button
                          type='button'
                          title='Edit this pre-mapping'
                          className='text-muted-foreground hover:text-foreground'
                          onClick={() => setEditing(n)}
                        >
                          <Pencil className='h-4 w-4' />
                        </button>
                        <button
                          type='button'
                          title='Delete. Mappings that use this field keep working.'
                          className='text-muted-foreground hover:text-destructive'
                          onClick={() => removeName.mutate(n.id)}
                        >
                          <Trash2 className='h-4 w-4' />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/** Choose a field from the flattened example, name it, see its example value. */
function PreMappingForm({
  schema,
  paths,
  row,
  onDone,
}: {
  schema: LisSourceSchema
  paths: FlatPath[]
  row: LisNamedPath | null
  onDone: () => void
}) {
  const [path, setPath] = useState(row?.path ?? '')
  const [name, setName] = useState(row?.name ?? '')
  const create = useCreateLisNamedPath()
  const update = useUpdateLisNamedPath()
  const saving = create.isPending || update.isPending
  const error = create.error ?? update.error

  // A field that already has a name is not offered again — one name per field.
  // Except this row's own, when editing.
  const taken = new Set(schema.namedPaths.filter((n) => n.id !== row?.id).map((n) => n.path))
  // Only NESTED fields. A top-level field (`testCode`, `orderId`) is offered on
  // the Mapping tab as it is, so naming it would just list it twice; a name earns
  // its place on a path like `observations[*].code.code`.
  const options = paths
    .filter((p) => !p.topLevel && !taken.has(p.path))
    .map((p) => ({ value: p.path, label: p.path }))
  // An edited name whose field the example no longer has must still show.
  if (row && !options.some((o) => o.value === row.path)) {
    options.unshift({ value: row.path, label: row.path })
  }
  const sample = paths.find((p) => p.path === path)?.sample

  const save = async () => {
    if (!path || !name.trim()) return
    if (row) await update.mutateAsync({ id: row.id, name: name.trim(), path })
    else await create.mutateAsync({ schemaId: schema.id, name: name.trim(), path })
    onDone()
  }

  return (
    <div className='border-b bg-muted/30 px-4 py-4'>
      <h4 className='mb-3 text-sm font-semibold'>
        {row ? `Edit ${row.name}` : 'Create pre-mapping'}
      </h4>
      <div className='grid gap-3 sm:grid-cols-[2fr_1fr_1fr]'>
        <div className='grid gap-1.5'>
          <Label className='text-xs'>Field</Label>
          <SearchableSelect
            value={path}
            onChange={setPath}
            options={options}
            placeholder='Choose a field from the example'
          />
        </div>
        <div className='grid gap-1.5'>
          <Label htmlFor='lis-premapping-name' className='text-xs'>
            Name
          </Label>
          <Input
            id='lis-premapping-name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='e.g. obsCode'
            onKeyDown={(e) => {
              if (e.key === 'Enter') void save()
            }}
          />
        </div>
        <div className='grid gap-1.5'>
          <Label className='text-xs'>Example value</Label>
          <p
            className='flex h-9 items-center truncate rounded-md border bg-background px-3 font-mono text-xs text-muted-foreground'
            title={sample}
          >
            {path ? (sample ?? 'not in the example') : '—'}
          </p>
        </div>
      </div>

      {error ? <p className='mt-2 text-sm text-destructive'>{error.message}</p> : null}

      <div className='mt-3 flex gap-2'>
        <Button size='sm' onClick={save} disabled={saving || !path || !name.trim()}>
          {saving ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
          Save
        </Button>
        <Button size='sm' variant='ghost' onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
